
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "sharing-a-candidate"
    }}>{`Sharing a candidate`}</h1>
    <p>{`The details of a candidate can be uploaded to the SEEK API.
This information is used to identify candidates that exist in your software and in SEEK’s Talent Search database when returning search results,
reducing the time hirers spend cross-referencing candidate information with your software.
Candidates that exist in both systems are referred to as shared candidates.`}</p>
    <p>{`Shared candidates shown on the Talent Search website will have a badge indicating that they are shared,
the date the candidate was last updated in your software and on SEEK,
and a deep link back to your software.`}</p>
    <p>{`Talent Search requires that changes to candidate information within your software be reflected in the SEEK API `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au/partners/terms-of-use/#talent-search-connect"
      }}>{`within 60 seconds`}</a>{`.`}</p>
    <p><img alt="Shared candidate results" data-scoobie-style="default" height="631" src={require('./assets/SharedCandidatesSERP.svg')} title="Shared & SEEK Profiles" width="850" /></p>
    <h2 {...{
      "id": "data-structure"
    }}>{`Data structure`}</h2>
    <p>{`An uploaded `}<a parentName="p" {...{
        "href": "/schema/named-type/Candidate"
      }}><inlineCode parentName="a">{`Candidate`}</inlineCode></a>{` object is structured as follows:`}</p>
    <img alt="" data-scoobie-style="none" height="252" src={require('../../../../mermaid/.de46c138009852478819f2eebd4f35bb37c9ad77.mmd.svg')} title="Uploaded candidate objects" width="573" />
    <p>{`An uploaded candidate has the following `}<a parentName="p" {...{
        "href": "/schema/named-type/CandidatePerson"
      }}><inlineCode parentName="a">{`CandidatePerson`}</inlineCode></a>{` fields:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Name`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Email addresses, with at least one required`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Optional phone numbers`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Optional home locations`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Whether they can be contacted`}</p>
      </li>
    </ul>
    <p>{`Each uploaded candidate has a single `}<a parentName="p" {...{
        "href": "/schema/named-type/CandidateProfile"
      }}><inlineCode parentName="a">{`CandidateProfile`}</inlineCode></a>{` containing:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Create and update dates`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Profile actions that deep link into your software`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Preferred work locations`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "/use-cases/proactive-sourcing/interaction-history"
          }}>{`Interaction history`}</a></p>
      </li>
    </ul>
    <p>{`Each uploaded candidate is scoped to a specific SEEK hirer and integration partner.
A candidate uploaded via your software is only accessible to that hirer,
and is not accessible to other integration partners who may have a relationship with the hirer.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <h3 {...{
      "id": "upload-a-candidate"
    }}>{`Upload a candidate`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/uploadCandidate"
      }}><inlineCode parentName="a">{`uploadCandidate`}</inlineCode>{` mutation`}</a>{` adds a new candidate and their profile.
You should use this mutation whenever a new candidate is created in your software.
This includes candidates you create in your software using `}<a parentName="p" {...{
        "href": "/use-cases/proactive-sourcing/profile-purchases"
      }}>{`profiles purchased`}</a>{` from Talent Search.`}</p>
    <p>{`To avoid unintentional duplication of data, SEEK enforces uniqueness on an email address of the candidate.
Requests to share a candidate require a `}<a parentName="p" {...{
        "href": "/schema/named-type/UploadCandidate_CandidateInput#/field/seekPrimaryEmailAddress"
      }}><inlineCode parentName="a">{`seekPrimaryEmailAddress`}</inlineCode>{` field`}</a>{` with a value that matches one of the given email address values in the `}<a parentName="p" {...{
        "href": "/schema/named-type/CandidatePersonInput#/field/communication"
      }}><inlineCode parentName="a">{`person.communication`}</inlineCode>{` field`}</a>{`.
A request to upload an existing candidate will receive an `}<inlineCode parentName="p">{`UploadCandidatePayload_Conflict`}</inlineCode>{` result.
The existing candidate will not be updated; you can select their details from the conflict result.`}</p>
    <p>{`If a candidate has expressed a preference to not be contacted,
you should upload them with a `}<inlineCode parentName="p">{`seekDoNotContactIndicator`}</inlineCode>{` value of `}<inlineCode parentName="p">{`true`}</inlineCode>{` in the `}<a parentName="p" {...{
        "href": "/schema/named-type/CandidatePersonInput#/field/communication"
      }}><inlineCode parentName="a">{`person.communication`}</inlineCode>{` field`}</a>{`.
This will customise the display name of the candidate in SEEK Talent Search and prevent hirers from contacting them.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "center"
          }}><p parentName="th"><strong parentName="p"><inlineCode parentName="strong">{`seekDoNotContactIndicator`}</inlineCode></strong></p></th>
          <th parentName="tr" {...{
            "align": "left"
          }}><p parentName="th"><strong parentName="p">{`Display name`}</strong></p></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><inlineCode parentName="p">{`null`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Hana Webber`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><inlineCode parentName="p">{`false`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Hana Webber`}</p></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "center"
          }}><p parentName="td"><inlineCode parentName="p">{`true`}</inlineCode></p></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><p parentName="td">{`Hana Webber has elected not to be contacted by `}<inlineCode parentName="p">{`Partner`}</inlineCode></p></td>
        </tr>
      </tbody>
    </table>
    <p>{`Up to 20 `}<a parentName="p" {...{
        "href": "/use-cases/proactive-sourcing/interaction-history"
      }}>{`interaction history`}</a>{` items can be provided in the `}<inlineCode parentName="p">{`seekProcessHistory`}</inlineCode>{` field during initial upload.
Individual items can be later created, modified and deleted using the relevant `}<a parentName="p" {...{
        "href": "/use-cases/proactive-sourcing/interaction-history#operations"
      }}>{`interaction history operations`}</a>{`.
SEEK strongly recommends providing the first 20 interaction history items when backfilling an existing candidate.
This maximises the rate your software can backfill candidates while reducing load on SEEK’s systems.`}</p>
    <p>{`Keep track of the IDs assigned to the candidate, their profile and their interaction history to perform subsequent update and delete operations.
The `}<a parentName="p" {...{
        "href": "/schema/named-type/UploadCandidatePayload_Success#/field/candidateProcessHistoryItems"
      }}><inlineCode parentName="a">{`UploadCandidatePayload_Success.candidateProcessHistoryItems`}</inlineCode>{` field`}</a>{` contains the uploaded interaction history in input order for ease of correlation.`}</p>
    <pre><code parentName="pre" {...{
        "5": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"Mutation\\\"\",\"value\":\"mutation ($input: UploadCandidateInput!) {\\n  uploadCandidate(input: $input) {\\n    ... on UploadCandidatePayload_Success {\\n      candidate {\\n        documentId {\\n          value\\n        }\\n        profiles {\\n          profileId {\\n            value\\n          }\\n        }\\n      }\\n      candidateProcessHistoryItems {\\n        id {\\n          value\\n        }\\n      }\\n    }\\n\\n    ... on UploadCandidatePayload_Conflict {\\n      conflictingCandidate {\\n        documentId {\\n          value\\n        }\\n        profiles {\\n          profileId {\\n            value\\n          }\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":4154},\"end\":{\"line\":114,\"column\":4,\"offset\":4774},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Variables\\\"\",\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"candidate\\\": {\\n      \\\"seekPrimaryEmailAddress\\\": \\\"Marcus_Damore@gmail.com\\\",\\n      \\\"person\\\": {\\n        \\\"communication\\\": {\\n          \\\"address\\\": [\\n            {\\n              \\\"city\\\": \\\"Melbourne\\\",\\n              \\\"countryCode\\\": \\\"AU\\\",\\n              \\\"countrySubDivisions\\\": [{ \\\"type\\\": \\\"State\\\", \\\"value\\\": \\\"AU-VIC\\\" }],\\n              \\\"extendedLines\\\": [\\n                { \\\"type\\\": \\\"Apartment\\\", \\\"value\\\": \\\"Apartment 5\\\" }\\n              ],\\n              \\\"formattedAddress\\\": \\\"Apartment 3, 5 Fake St\\\\nWindsor VIC 3181\\\",\\n              \\\"geoLocation\\\": {\\n                \\\"latitude\\\": -37.854,\\n                \\\"longitude\\\": 144.988\\n              },\\n              \\\"line\\\": \\\"5 Fake St\\\",\\n              \\\"postalCode\\\": \\\"3181\\\"\\n            }\\n          ],\\n          \\\"seekDoNotContactIndicator\\\": false,\\n          \\\"email\\\": [\\n            {\\n              \\\"address\\\": \\\"Marcus_Damore@gmail.com\\\"\\n            }\\n          ],\\n          \\\"phone\\\": []\\n        },\\n        \\\"name\\\": {\\n          \\\"formattedName\\\": \\\"Marcus Damore\\\",\\n          \\\"family\\\": \\\"Damore\\\",\\n          \\\"given\\\": \\\"Marcus\\\"\\n        }\\n      },\\n      \\\"distributionGuidelines\\\": {\\n        \\\"seekProductCodes\\\": [\\\"ProactiveSourcing\\\"]\\n      }\\n    },\\n    \\\"candidateProfile\\\": {\\n      \\\"createDateTime\\\": \\\"2006-01-02T15:04:05Z\\\",\\n      \\\"updateDateTime\\\": \\\"2020-08-08T15:04:05.000Z\\\",\\n      \\\"seekProcessHistory\\\": [],\\n      \\\"positionPreferences\\\": [],\\n      \\\"seekActions\\\": [\\n        {\\n          \\\"code\\\": \\\"ViewProfile\\\",\\n          \\\"seekUrl\\\": {\\n            \\\"url\\\": \\\"https://example.com/profiles/a1b2c3d4\\\"\\n          }\\n        }\\n      ]\\n    },\\n    \\\"hiringOrganization\\\": {\\n      \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":116,\"column\":1,\"offset\":4776},\"end\":{\"line\":177,\"column\":4,\"offset\":6446},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Success result\\\"\",\"value\":\"{\\n  \\\"uploadCandidate\\\": {\\n    // Details of the newly uploaded candidate\\n    \\\"candidate\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n      },\\n      \\\"profiles\\\": [\\n        {\\n          \\\"profileId\\\": {\\n            \\\"value\\\": \\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n          }\\n        }\\n      ]\\n    },\\n    \\\"candidateProcessHistoryItems\\\": []\\n  }\\n}\",\"position\":{\"start\":{\"line\":179,\"column\":1,\"offset\":6448},\"end\":{\"line\":198,\"column\":4,\"offset\":6909},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Conflict result\\\"\",\"value\":\"{\\n  \\\"uploadCandidate\\\": {\\n    // Details of the conflicting candidate\\n    \\\"conflictingCandidate\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n      },\\n      \\\"profiles\\\": [\\n        {\\n          \\\"profileId\\\": {\\n            \\\"value\\\": \\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n          }\\n        }\\n      ]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":200,\"column\":1,\"offset\":6911},\"end\":{\"line\":218,\"column\":4,\"offset\":7341},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"Mutation\\\"\",\"value\":\"mutation",
        "($input:": true,
        "UploadCandidateInput!)": true,
        "{\\n": true,
        "": true,
        "uploadCandidate(input:": true,
        "$input)": true,
        "...": true,
        "on": true,
        "UploadCandidatePayload_Success": true,
        "candidate": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "profiles": true,
        "profileId": true,
        "candidateProcessHistoryItems": true,
        "id": true,
        "}\\n\\n": true,
        "UploadCandidatePayload_Conflict": true,
        "conflictingCandidate": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":4154},\"end\":{\"line\":114,\"column\":4,\"offset\":4774},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Variables\\\"\",\"value\":\"{\\n",
        "\\\"input\\\":": true,
        "\\\"candidate\\\":": true,
        "\\\"seekPrimaryEmailAddress\\\":": true,
        "\\\"Marcus_Damore@gmail.com\\\",\\n": true,
        "\\\"person\\\":": true,
        "\\\"communication\\\":": true,
        "\\\"address\\\":": true,
        "[\\n": true,
        "\\\"city\\\":": true,
        "\\\"Melbourne\\\",\\n": true,
        "\\\"countryCode\\\":": true,
        "\\\"AU\\\",\\n": true,
        "\\\"countrySubDivisions\\\":": true,
        "[{": true,
        "\\\"type\\\":": true,
        "\\\"State\\\",": true,
        "\\\"value\\\":": true,
        "\\\"AU-VIC\\\"": true,
        "}],\\n": true,
        "\\\"extendedLines\\\":": true,
        "{": true,
        "\\\"Apartment\\\",": true,
        "\\\"Apartment": true,
        "5\\\"": true,
        "],\\n": true,
        "\\\"formattedAddress\\\":": true,
        "3,": true,
        "Fake": true,
        "St\\\\nWindsor": true,
        "VIC": true,
        "3181\\\",\\n": true,
        "\\\"geoLocation\\\":": true,
        "\\\"latitude\\\":": true,
        "-37.854,\\n": true,
        "\\\"longitude\\\":": true,
        "144.988\\n": true,
        "},\\n": true,
        "\\\"line\\\":": true,
        "\\\"5": true,
        "St\\\",\\n": true,
        "\\\"postalCode\\\":": true,
        "\\\"3181\\\"\\n": true,
        "\\\"seekDoNotContactIndicator\\\":": true,
        "false,\\n": true,
        "\\\"email\\\":": true,
        "\\\"Marcus_Damore@gmail.com\\\"\\n": true,
        "\\\"phone\\\":": true,
        "[]\\n": true,
        "\\\"name\\\":": true,
        "\\\"formattedName\\\":": true,
        "\\\"Marcus": true,
        "Damore\\\",\\n": true,
        "\\\"family\\\":": true,
        "\\\"Damore\\\",\\n": true,
        "\\\"given\\\":": true,
        "\\\"Marcus\\\"\\n": true,
        "\\\"distributionGuidelines\\\":": true,
        "\\\"seekProductCodes\\\":": true,
        "[\\\"ProactiveSourcing\\\"]\\n": true,
        "\\\"candidateProfile\\\":": true,
        "\\\"createDateTime\\\":": true,
        "\\\"2006-01-02T15:04:05Z\\\",\\n": true,
        "\\\"updateDateTime\\\":": true,
        "\\\"2020-08-08T15:04:05.000Z\\\",\\n": true,
        "\\\"seekProcessHistory\\\":": true,
        "[],\\n": true,
        "\\\"positionPreferences\\\":": true,
        "\\\"seekActions\\\":": true,
        "\\\"code\\\":": true,
        "\\\"ViewProfile\\\",\\n": true,
        "\\\"seekUrl\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://example.com/profiles/a1b2c3d4\\\"\\n": true,
        "]\\n": true,
        "\\\"hiringOrganization\\\":": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":116,\"column\":1,\"offset\":4776},\"end\":{\"line\":177,\"column\":4,\"offset\":6446},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Success",
        "result\\\"\",\"value\":\"{\\n": true,
        "\\\"uploadCandidate\\\":": true,
        "//": true,
        "Details": true,
        "of": true,
        "the": true,
        "newly": true,
        "uploaded": true,
        "candidate\\n": true,
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n": true,
        "\\\"profiles\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n": true,
        "\\\"candidateProcessHistoryItems\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":179,\"column\":1,\"offset\":6448},\"end\":{\"line\":198,\"column\":4,\"offset\":6909},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Conflict",
        "conflicting": true,
        "\\\"conflictingCandidate\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":200,\"column\":1,\"offset\":6911},\"end\":{\"line\":218,\"column\":4,\"offset\":7341},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: UploadCandidateInput!) {
  uploadCandidate(input: $input) {
    ... on UploadCandidatePayload_Success {
      candidate {
        documentId {
          value
        }
        profiles {
          profileId {
            value
          }
        }
      }
      candidateProcessHistoryItems {
        id {
          value
        }
      }
    }

    ... on UploadCandidatePayload_Conflict {
      conflictingCandidate {
        documentId {
          value
        }
        profiles {
          profileId {
            value
          }
        }
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "update-an-uploaded-candidate"
    }}>{`Update an uploaded candidate`}</h3>
    <p>{`The following mutations update an uploaded candidate and their profile.
You should use these mutations whenever an existing candidate is updated in your software.
This includes candidates you update in your software using `}<a parentName="p" {...{
        "href": "/use-cases/proactive-sourcing/profile-purchases"
      }}>{`profiles purchased`}</a>{` from Talent Search.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/schema/mutation/updateUploadedCandidatePerson"
          }}><inlineCode parentName="a">{`updateUploadedCandidatePerson`}</inlineCode>{` mutation`}</a>{` replaces personal details.`}</p>
        <p parentName="li">{`A request to update the candidate’s email with an existing `}<inlineCode parentName="p">{`seekPrimaryEmailAddress`}</inlineCode>{` will receive a `}<inlineCode parentName="p">{`UpdateUploadedCandidatePersonPayload_Conflict`}</inlineCode>{`.
The existing candidate will not be updated; you can select the conflicting candidate details from the conflict result.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/schema/mutation/updateUploadedCandidateProfileActions"
          }}><inlineCode parentName="a">{`updateUploadedCandidateProfileActions`}</inlineCode>{` mutation`}</a>{` replaces profile actions.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/schema/mutation/updateUploadedCandidateProfileDates"
          }}><inlineCode parentName="a">{`updateUploadedCandidateProfileDates`}</inlineCode>{` mutation`}</a>{` replaces profile create and update dates.`}</p>
        <p parentName="li">{`The profile’s update date is intended to reflect when the candidate has been updated in your software.
This mutation should be used even if the update isn’t captured by the profile fields you upload to SEEK.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "/schema/mutation/updateUploadedCandidateProfilePositionPreferences"
          }}><inlineCode parentName="a">{`updateUploadedCandidateProfilePositionPreferences`}</inlineCode>{` mutation`}</a>{` replaces profile position preferences.`}</p>
      </li>
    </ul>
    <p>{`These mutations can be `}<a parentName="p" {...{
        "href": "/graphql/in-practice#request-batching"
      }}>{`batched`}</a>{` together in a single request for sync-like behaviour:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"Mutation\\\"\",\"value\":\"mutation updateUploadedCandidate(\\n  $documentId: String!\\n  $person: CandidatePersonInput!\\n  $seekPrimaryEmailAddress: String!\\n  $profileId: String!\\n  $seekActions: [CandidateProcessActionInput!]!\\n  $createDateTime: DateTime!\\n  $updateDateTime: DateTime!\\n  $positionPreferences: [PositionPreferenceInput!]!\\n) {\\n  updateUploadedCandidatePerson(\\n    input: {\\n      candidate: {\\n        documentId: $documentId\\n        person: $person\\n        seekPrimaryEmailAddress: $seekPrimaryEmailAddress\\n      }\\n    }\\n  ) {\\n    ... on UpdateUploadedCandidatePersonPayload_Success {\\n      candidate {\\n        documentId {\\n          value\\n        }\\n      }\\n    }\\n\\n    ... on UpdateUploadedCandidatePersonPayload_Conflict {\\n      conflictingCandidate {\\n        documentId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n\\n  updateUploadedCandidateProfileActions(\\n    input: {\\n      candidateProfile: { profileId: $profileId, seekActions: $seekActions }\\n    }\\n  ) {\\n    candidateProfile {\\n      candidate {\\n        documentId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n\\n  updateUploadedCandidateProfileDates(\\n    input: {\\n      candidateProfile: {\\n        profileId: $profileId\\n        createDateTime: $createDateTime\\n        updateDateTime: $updateDateTime\\n      }\\n    }\\n  ) {\\n    candidateProfile {\\n      candidate {\\n        documentId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n\\n  updateUploadedCandidateProfilePositionPreferences(\\n    input: {\\n      candidateProfile: {\\n        profileId: $profileId\\n        positionPreferences: $positionPreferences\\n      }\\n    }\\n  ) {\\n    candidateProfile {\\n      candidate {\\n        documentId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":246,\"column\":1,\"offset\":8888},\"end\":{\"line\":332,\"column\":4,\"offset\":10585},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Variables\\\"\",\"value\":\"{\\n  \\\"documentId\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\",\\n  \\\"seekPrimaryEmailAddress\\\": \\\"Marcus_Damore@newDomain.com\\\",\\n  \\\"person\\\": {\\n    \\\"communication\\\": {\\n      \\\"email\\\": [\\n        {\\n          \\\"address\\\": \\\"Marcus_Damore@newDomain.com\\\"\\n        }\\n      ],\\n      \\\"phone\\\": []\\n    },\\n    \\\"name\\\": {\\n      \\\"formattedName\\\": \\\"Mars Damore\\\",\\n      \\\"family\\\": \\\"Damore\\\",\\n      \\\"given\\\": \\\"Mars\\\"\\n    }\\n  },\\n  \\\"profileId\\\": \\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\",\\n  \\\"seekActions\\\": [],\\n  \\\"createDateTime\\\": \\\"1970-01-01T00:00:00Z\\\",\\n  \\\"updateDateTime\\\": \\\"2020-12-31T23:59:59.999Z\\\",\\n  \\\"positionPreferences\\\": [\\n    {\\n      \\\"locations\\\": [\\n        {\\n          \\\"referenceLocation\\\": {\\n            \\\"city\\\": \\\"Melbourne\\\",\\n            \\\"countryCode\\\": \\\"AU\\\",\\n            \\\"countrySubDivisions\\\": [],\\n            \\\"extendedLines\\\": [],\\n            \\\"postalCode\\\": \\\"3000\\\"\\n          }\\n        }\\n      ]\\n    }\\n  ]\\n}\",\"position\":{\"start\":{\"line\":334,\"column\":1,\"offset\":10587},\"end\":{\"line\":373,\"column\":4,\"offset\":11533},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Success result\\\"\",\"value\":\"{\\n  \\\"updateUploadedCandidatePerson\\\": {\\n    \\\"candidate\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n      }\\n    }\\n  },\\n  \\\"updateUploadedCandidateProfileActions\\\": {\\n    \\\"candidatePerson\\\": {\\n      \\\"candidate\\\": {\\n        \\\"documentId\\\": {\\n          \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n        }\\n      }\\n    }\\n  },\\n  \\\"updateUploadedCandidateProfileDates\\\": {\\n    \\\"candidatePerson\\\": {\\n      \\\"candidate\\\": {\\n        \\\"documentId\\\": {\\n          \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n        }\\n      }\\n    }\\n  },\\n  \\\"updateUploadedCandidateProfilePositionPreferences\\\": {\\n    \\\"candidatePerson\\\": {\\n      \\\"candidate\\\": {\\n        \\\"documentId\\\": {\\n          \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":375,\"column\":1,\"offset\":11535},\"end\":{\"line\":412,\"column\":4,\"offset\":12429},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label=\\\"Conflict result\\\"\",\"value\":\"{\\n  \\\"updateUploadedCandidatePerson\\\": {\\n    \\\"conflictingCandidate\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:LupBTN3BD6NTiuS419iRCa\\\"\\n      }\\n    }\\n  },\\n  \\\"updateUploadedCandidateProfileActions\\\": {\\n    \\\"candidatePerson\\\": {\\n      \\\"candidate\\\": {\\n        \\\"documentId\\\": {\\n          \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n        }\\n      }\\n    }\\n  },\\n  \\\"updateUploadedCandidateProfileDates\\\": {\\n    \\\"candidatePerson\\\": {\\n      \\\"candidate\\\": {\\n        \\\"documentId\\\": {\\n          \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n        }\\n      }\\n    }\\n  },\\n  \\\"updateUploadedCandidateProfilePositionPreferences\\\": {\\n    \\\"candidatePerson\\\": {\\n      \\\"candidate\\\": {\\n        \\\"documentId\\\": {\\n          \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":414,\"column\":1,\"offset\":12431},\"end\":{\"line\":451,\"column\":4,\"offset\":13337},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"Mutation\\\"\",\"value\":\"mutation",
        "updateUploadedCandidate(\\n": true,
        "": true,
        "$documentId:": true,
        "String!\\n": true,
        "$person:": true,
        "CandidatePersonInput!\\n": true,
        "$seekPrimaryEmailAddress:": true,
        "$profileId:": true,
        "$seekActions:": true,
        "[CandidateProcessActionInput!]!\\n": true,
        "$createDateTime:": true,
        "DateTime!\\n": true,
        "$updateDateTime:": true,
        "$positionPreferences:": true,
        "[PositionPreferenceInput!]!\\n)": true,
        "{\\n": true,
        "updateUploadedCandidatePerson(\\n": true,
        "input:": true,
        "candidate:": true,
        "documentId:": true,
        "$documentId\\n": true,
        "person:": true,
        "$person\\n": true,
        "seekPrimaryEmailAddress:": true,
        "$seekPrimaryEmailAddress\\n": true,
        "}\\n": true,
        ")": true,
        "...": true,
        "on": true,
        "UpdateUploadedCandidatePersonPayload_Success": true,
        "candidate": true,
        "documentId": true,
        "value\\n": true,
        "}\\n\\n": true,
        "UpdateUploadedCandidatePersonPayload_Conflict": true,
        "conflictingCandidate": true,
        "updateUploadedCandidateProfileActions(\\n": true,
        "candidateProfile:": true,
        "{": true,
        "profileId:": true,
        "$profileId,": true,
        "seekActions:": true,
        "$seekActions": true,
        "candidateProfile": true,
        "updateUploadedCandidateProfileDates(\\n": true,
        "$profileId\\n": true,
        "createDateTime:": true,
        "$createDateTime\\n": true,
        "updateDateTime:": true,
        "$updateDateTime\\n": true,
        "updateUploadedCandidateProfilePositionPreferences(\\n": true,
        "positionPreferences:": true,
        "$positionPreferences\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":246,\"column\":1,\"offset\":8888},\"end\":{\"line\":332,\"column\":4,\"offset\":10585},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Variables\\\"\",\"value\":\"{\\n",
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\",\\n": true,
        "\\\"seekPrimaryEmailAddress\\\":": true,
        "\\\"Marcus_Damore@newDomain.com\\\",\\n": true,
        "\\\"person\\\":": true,
        "\\\"communication\\\":": true,
        "\\\"email\\\":": true,
        "[\\n": true,
        "\\\"address\\\":": true,
        "\\\"Marcus_Damore@newDomain.com\\\"\\n": true,
        "],\\n": true,
        "\\\"phone\\\":": true,
        "[]\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"formattedName\\\":": true,
        "\\\"Mars": true,
        "Damore\\\",\\n": true,
        "\\\"family\\\":": true,
        "\\\"Damore\\\",\\n": true,
        "\\\"given\\\":": true,
        "\\\"Mars\\\"\\n": true,
        "\\\"profileId\\\":": true,
        "\\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\",\\n": true,
        "\\\"seekActions\\\":": true,
        "[],\\n": true,
        "\\\"createDateTime\\\":": true,
        "\\\"1970-01-01T00:00:00Z\\\",\\n": true,
        "\\\"updateDateTime\\\":": true,
        "\\\"2020-12-31T23:59:59.999Z\\\",\\n": true,
        "\\\"positionPreferences\\\":": true,
        "\\\"locations\\\":": true,
        "\\\"referenceLocation\\\":": true,
        "\\\"city\\\":": true,
        "\\\"Melbourne\\\",\\n": true,
        "\\\"countryCode\\\":": true,
        "\\\"AU\\\",\\n": true,
        "\\\"countrySubDivisions\\\":": true,
        "\\\"extendedLines\\\":": true,
        "\\\"postalCode\\\":": true,
        "\\\"3000\\\"\\n": true,
        "]\\n": true,
        "]\\n}\",\"position\":{\"start\":{\"line\":334,\"column\":1,\"offset\":10587},\"end\":{\"line\":373,\"column\":4,\"offset\":11533},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Success",
        "result\\\"\",\"value\":\"{\\n": true,
        "\\\"updateUploadedCandidatePerson\\\":": true,
        "\\\"candidate\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n": true,
        "\\\"updateUploadedCandidateProfileActions\\\":": true,
        "\\\"candidatePerson\\\":": true,
        "\\\"updateUploadedCandidateProfileDates\\\":": true,
        "\\\"updateUploadedCandidateProfilePositionPreferences\\\":": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":375,\"column\":1,\"offset\":11535},\"end\":{\"line\":412,\"column\":4,\"offset\":12429},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"label": "\\\"Conflict",
        "\\\"conflictingCandidate\\\":": true,
        "\\\"globalPublicTest:candidate:uploaded:LupBTN3BD6NTiuS419iRCa\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":414,\"column\":1,\"offset\":12431},\"end\":{\"line\":451,\"column\":4,\"offset\":13337},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation updateUploadedCandidate(
  $documentId: String!
  $person: CandidatePersonInput!
  $seekPrimaryEmailAddress: String!
  $profileId: String!
  $seekActions: [CandidateProcessActionInput!]!
  $createDateTime: DateTime!
  $updateDateTime: DateTime!
  $positionPreferences: [PositionPreferenceInput!]!
) {
  updateUploadedCandidatePerson(
    input: {
      candidate: {
        documentId: $documentId
        person: $person
        seekPrimaryEmailAddress: $seekPrimaryEmailAddress
      }
    }
  ) {
    ... on UpdateUploadedCandidatePersonPayload_Success {
      candidate {
        documentId {
          value
        }
      }
    }

    ... on UpdateUploadedCandidatePersonPayload_Conflict {
      conflictingCandidate {
        documentId {
          value
        }
      }
    }
  }

  updateUploadedCandidateProfileActions(
    input: {
      candidateProfile: { profileId: $profileId, seekActions: $seekActions }
    }
  ) {
    candidateProfile {
      candidate {
        documentId {
          value
        }
      }
    }
  }

  updateUploadedCandidateProfileDates(
    input: {
      candidateProfile: {
        profileId: $profileId
        createDateTime: $createDateTime
        updateDateTime: $updateDateTime
      }
    }
  ) {
    candidateProfile {
      candidate {
        documentId {
          value
        }
      }
    }
  }

  updateUploadedCandidateProfilePositionPreferences(
    input: {
      candidateProfile: {
        profileId: $profileId
        positionPreferences: $positionPreferences
      }
    }
  ) {
    candidateProfile {
      candidate {
        documentId {
          value
        }
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "delete-an-uploaded-candidate"
    }}>{`Delete an uploaded candidate`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/schema/mutation/deleteUploadedCandidate"
      }}><inlineCode parentName="a">{`deleteUploadedCandidate`}</inlineCode>{` mutation`}</a>{` deletes an uploaded candidate and their profile.`}</p>
    <p>{`This will erase all uploaded details of the candidate from SEEK’s systems.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: DeleteUploadedCandidateInput!) {\\n  deleteUploadedCandidate(input: $input) {\\n    candidate {\\n      documentId {\\n        value\\n      }\\n      profiles {\\n        profileId {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":459,\"column\":1,\"offset\":13540},\"end\":{\"line\":474,\"column\":4,\"offset\":13788},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"candidate\\\": {\\n      \\\"documentId\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":476,\"column\":1,\"offset\":13790},\"end\":{\"line\":484,\"column\":4,\"offset\":13928},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"deleteUploadedCandidate\\\": {\\n    \\\"candidate\\\": {\\n      \\\"documentId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n      },\\n      \\\"profiles\\\": [\\n        {\\n          \\\"profileId\\\": {\\n            \\\"value\\\": \\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n          }\\n        }\\n      ]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":486,\"column\":1,\"offset\":13930},\"end\":{\"line\":503,\"column\":4,\"offset\":14288},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "DeleteUploadedCandidateInput!)": true,
        "{\\n": true,
        "": true,
        "deleteUploadedCandidate(input:": true,
        "$input)": true,
        "candidate": true,
        "documentId": true,
        "value\\n": true,
        "}\\n": true,
        "profiles": true,
        "profileId": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":459,\"column\":1,\"offset\":13540},\"end\":{\"line\":474,\"column\":4,\"offset\":13788},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"candidate\\\":": true,
        "\\\"documentId\\\":": true,
        "\\\"globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":476,\"column\":1,\"offset\":13790},\"end\":{\"line\":484,\"column\":4,\"offset\":13928},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"deleteUploadedCandidate\\\":": true,
        "\\\"value\\\":": true,
        "},\\n": true,
        "\\\"profiles\\\":": true,
        "[\\n": true,
        "\\\"profileId\\\":": true,
        "\\\"globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV\\\"\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":486,\"column\":1,\"offset\":13930},\"end\":{\"line\":503,\"column\":4,\"offset\":14288},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: DeleteUploadedCandidateInput!) {
  deleteUploadedCandidate(input: $input) {
    candidate {
      documentId {
        value
      }
      profiles {
        profileId {
          value
        }
      }
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;